import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Banknote, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Plus, Download } from "lucide-react";

const STATUS_CONFIG = {
  ok: { label: "Saludable", color: "bg-green-100 text-green-800 border-green-200", dot: "bg-green-500" },
  ajustado: { label: "Ajustado", color: "bg-yellow-100 text-yellow-800 border-yellow-200", dot: "bg-yellow-500" },
  critico: { label: "Critico", color: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" },
};

function WeekCard({ proj, branchName }) {
  const cfg = STATUS_CONFIG[proj.status] || STATUS_CONFIG.ajustado;
  const total_egresos = (proj.egresos_comprometidos || []).reduce((s, e) => s + (e.amount || 0), 0);
  const ing_conf = proj.ingresos_confirmados?.total || 0;
  const ing_proy = proj.ingresos_proyectados?.total || 0;
  const displayName = proj.branch_id === "consolidated" ? "Consolidado" : (branchName || proj.branch_id.slice(0, 8));

  return (
    <div className={`bg-white rounded-xl border p-5 space-y-4 ${proj.status === "critico" ? "border-red-200" : "border-slate-200"}`}
      data-testid={`cash-flow-card-${proj.branch_id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-700">{displayName}</div>
          <div className="text-xs text-slate-500">{proj.week_start} — {proj.week_end}</div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full border font-medium ${cfg.color}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${cfg.dot}`}></span>
          {cfg.label}
        </span>
      </div>

      {proj.status === "critico" && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid={`cash-flow-alert-banner-${proj.branch_id}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Saldo por debajo del minimo configurado (${(proj.saldo_minimo_configurado || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}). Se generó una alerta interna.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-0.5">Ingresos confirmados</div>
          <div className="font-bold text-slate-900">${ing_conf.toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-0.5">Ingresos proyectados</div>
          <div className="font-bold text-blue-700">${ing_proy.toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <div className="text-xs text-slate-500 mb-0.5">Egresos comprometidos</div>
          <div className="font-bold text-orange-700">${total_egresos.toLocaleString("es-MX", { maximumFractionDigits: 0 })}</div>
        </div>
        <div className={`rounded-lg p-3 ${proj.saldo_proyectado_fin_semana >= 0 ? "bg-green-50" : "bg-red-50"}`}>
          <div className="text-xs text-slate-500 mb-0.5">Saldo proyectado</div>
          <div className={`font-bold ${proj.saldo_proyectado_fin_semana >= 0 ? "text-green-700" : "text-red-700"}`}>
            ${(proj.saldo_proyectado_fin_semana || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      <div className="text-xs text-slate-400" data-testid={`cash-flow-minimo-${proj.branch_id}`}>
        Minimo configurado: ${(proj.saldo_minimo_configurado || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}
      </div>

      {proj.egresos_comprometidos?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1.5">Egresos detallados</div>
          <div className="space-y-1">
            {proj.egresos_comprometidos.slice(0, 4).map((eg, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-slate-600 capitalize">{eg.category} {eg.reference ? `— ${eg.reference}` : (eg.description ? `— ${eg.description}` : "")}</span>
                <span className="font-medium">${(eg.amount || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}</span>
              </div>
            ))}
            {proj.egresos_comprometidos.length > 4 && (
              <div className="text-xs text-slate-400">+{proj.egresos_comprometidos.length - 4} mas...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AddCommitmentModal({ onClose, onSave, branches }) {
  const [form, setForm] = useState({
    branch_id: branches[0]?.id || "",
    category: "sueldos",
    amount: "",
    due_date: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.amount || !form.due_date || !form.branch_id) return;
    setLoading(true);
    try {
      await api.post("/ops/cash-flow/manual-commitment", {
        ...form, amount: parseFloat(form.amount),
      });
      onSave();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-slate-900">Agregar egreso manual</h3>
        {[
          { label: "Sucursal", field: "branch_id", type: "select", options: branches.map(b => ({ value: b.id, label: b.name })) },
          { label: "Categoria", field: "category", type: "select", options: ["sueldos","alquiler","servicios","otros"].map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) })) },
          { label: "Monto", field: "amount", type: "number", placeholder: "0.00" },
          { label: "Fecha de pago", field: "due_date", type: "date" },
          { label: "Descripcion", field: "description", type: "text", placeholder: "Opcional" },
        ].map(({ label, field, type, placeholder, options }) => (
          <div key={field}>
            <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
            {type === "select" ? (
              <select value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input type={type} value={form[field]} placeholder={placeholder}
                onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            )}
          </div>
        ))}
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
          <button onClick={handleSubmit} disabled={loading}
            className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 disabled:opacity-50">
            {loading ? "Guardando..." : "Agregar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OpsCashFlow() {
  const { user } = useAuth();
  const [projections, setProjections] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfRes, brRes] = await Promise.all([
        api.get("/ops/cash-flow"),
        api.get("/branches"),
      ]);
      setProjections(cfRes.data || []);
      setBranches(brRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await api.post("/ops/cash-flow/recompute");
      await fetchData();
    } catch (e) { console.error(e); }
    finally { setRecomputing(false); }
  };

  const handleExport = async () => {
    try {
      const resp = await api.get("/ops/cash-flow/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "cash_flow.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="cash-flow-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Banknote className="w-6 h-6 text-yellow-500" /> Flujo de caja semanal
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Proyeccion de ingresos y egresos por semana</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
            <Plus className="w-4 h-4" /> Egreso manual
          </button>
          <button onClick={handleRecompute} disabled={recomputing} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${recomputing ? "animate-spin" : ""}`} /> Recalcular
          </button>
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> Exportar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : projections.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Banknote className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-600">No hay proyecciones calculadas</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">Hace click en "Recalcular" para generar las proyecciones de la semana actual.</p>
          <button onClick={handleRecompute} disabled={recomputing}
            className="px-6 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors disabled:opacity-50">
            {recomputing ? "Calculando..." : "Calcular ahora"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projections.map(proj => <WeekCard key={proj.id || proj.branch_id} proj={proj} branchName={branches.find(b => b.id === proj.branch_id)?.name} />)}
        </div>
      )}

      {showModal && (
        <AddCommitmentModal
          branches={branches}
          onClose={() => setShowModal(false)}
          onSave={fetchData}
        />
      )}
    </div>
  );
}
